import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "du-csas-round-2-allotment-2026",
  title: "DU CSAS 2026 Round 2: Allotment Breakdown, Revised Schedule, and Decision Matrix",
  description:
    "Delhi University released the second CSAS UG seat allotment with 24,814 fresh allotments and a revised acceptance timeline. Step-by-step Upgrade vs Freeze decision flowchart, document checklist, and next steps.",
  pubDate: "2026-07-25",
  updatedDate: "2026-07-28",
  authorSlug: "uday-kumar",
  category: "news",
  tags: ["DU admissions", "CSAS 2026", "seat allotment", "UG admission", "decision-matrix"],
  readMinutes: 14,
  heroEyebrow: "News · DU Admissions Master Guide",
};

export function Body() {
  return (
    <>
      <p>
        Delhi University released the second UG seat allotment list under the Common Seat Allocation System (CSAS) on July 25, 2026, on the official admissions portal at{" "}
        <a
          href="https://admission.uod.ac.in/"
          target="_blank"
          rel="noopener noreferrer"
        >
          admission.uod.ac.in
        </a>
        . According to reports across leading education outlets including NDTV, Moneycontrol, and Jagran Josh, Round 2 saw 24,814 fresh seat allotments. Candidates must verify their individual status on the{" "}
        <a
          href="https://admission.uod.ac.in/?UG-Admissions"
          target="_blank"
          rel="noopener noreferrer"
        >
          DU CSAS UG Portal
        </a>
        .
      </p>

      <h2>1. Revised CSAS Round 2 Schedule &amp; Key Deadlines</h2>
      <p>
        Following the initial allotment release, Delhi University updated the Round 2 timeline to give candidates and college verification committees additional time. As reported in secondary media, the binding schedule shown on your CSAS dashboard operates as follows:
      </p>
      <ul>
        <li><strong>Round 2 Allotment Released:</strong> July 25, 2026</li>
        <li><strong>Deadline to Accept Allotted Seat:</strong> Sunday, July 27, 2026 (11.59 pm)</li>
        <li><strong>College Verification &amp; Document Approval:</strong> Tuesday, July 28, 2026 (5.00 pm)</li>
        <li><strong>Online Fee Payment Window Closes:</strong> Tuesday, July 28, 2026 (11.59 pm)</li>
        <li><strong>Commencement of Classes:</strong> Monday, July 28, 2026</li>
      </ul>

      <h2>2. Candidate Action Matrix: Upgrade vs. Freeze vs. Withdraw</h2>
      <div className="space-y-4 my-6 border border-rule p-5 bg-card text-sm">
        <div className="font-semibold text-base text-grade">CSAS Portal Decision Flowchart</div>
        <ol className="list-decimal list-inside space-y-3">
          <li>
            <strong>Option A: Accept &amp; Freeze</strong>
            <br />
            Select this if you are satisfied with your college and course combination. Once frozen, your seat is permanently secured and you are removed from subsequent upgrade rounds.
          </li>
          <li>
            <strong>Option B: Accept &amp; Upgrade</strong>
            <br />
            Select this if you wish to retain your currently allotted seat while remaining eligible for higher preferences in Round 3 or Spot Rounds. <em>Crucial Guarantee:</em> Upgrading never surrenders your current seat unless a higher preference is successfully allotted.
          </li>
          <li>
            <strong>Option C: Withdraw</strong>
            <br />
            Surrenders your seat with a partial fee refund per DU refund rules published on{" "}
            <a href="https://admission.uod.ac.in/" target="_blank" rel="noopener noreferrer">
              admission.uod.ac.in
            </a>
            .
          </li>
        </ol>
      </div>

      <h2>3. Document Verification Checklist for College Admissions</h2>
      <ul className="space-y-1 font-mono text-sm border border-rule p-4 bg-card">
        <li>✓ Class X Marksheet &amp; Passing Certificate (Age proof)</li>
        <li>✓ Class XII Marksheet &amp; Migration Certificate</li>
        <li>✓ CUET UG 2026 Scorecard (downloaded from NTA portal)</li>
        <li>✓ Category Certificate (SC/ST/OBC-NCL/EWS) issued after March 31, 2026</li>
        <li>✓ Government-issued Photo ID (Aadhaar / Passport)</li>
      </ul>

      <h2>4. Related Academic Guides</h2>
      <p>
        Before locking your degree preference, read our detailed comparison on{" "}
        <a href="/blog/honours-vs-programme" className="font-semibold underline text-grade">
          DU Honours vs BA Programme Differences
        </a>{" "}
        and review{" "}
        <a href="/blog/du-grading-attendance-explained" className="font-semibold underline text-grade">
          DU's Grading and Attendance System
        </a>
        .
      </p>

      <div className="my-8 border border-grade bg-card p-5 text-sm">
        <strong className="mono-label text-grade">Primary Official Portal Notice</strong>
        <p className="mt-2 text-pencil">
          All admission dates, cutoff lists, and fee timestamps must be verified directly on your CSAS candidate dashboard at{" "}
          <a href="https://admission.uod.ac.in/" target="_blank" rel="noopener noreferrer">
            admission.uod.ac.in
          </a>
          .
        </p>
      </div>
    </>
  );
}

